<template>
  <div>
    <a-table
      class="table"
      :columns="referralProgramTableColumn"
      :data-source="dataSource"
      :loading="loadingTable"
      size="medium"
      row-key="id"
      :scroll="scrollStyle"
      :pagination="pagination"
      @change="handleTableChange"
    >
      <!-- estado vacío -->
      <template #emptyText>
        <div class="empty-referrals">
          <div class="empty-icon" aria-hidden="true">
            <!-- Ícono simple (puedes cambiarlo por una imagen propia) -->
            <a-empty :image="simpleImage">
              <template #description>
                <p class="empty-text">
                  Aún no tienes referidos. Para referir y ganar giftcards haz
                  <a @click.prevent="emit('openRegisterNewBenefit')"> click aquí.</a>
                </p>
              </template>
            </a-empty>
          </div>
          
        </div>
      </template>

      <!-- slots existentes -->
      <template #state="{ record }">
        <a-tag :color="ReferredStatusColor[record.state as ReferredStatus]">
          <component :is="ReferredStatusIcon[record.state as ReferredStatus]" style="margin-right: 4px;" />
          {{ ReferredStatusText[record.state as ReferredStatus] }}
        </a-tag>
      </template>

      <template #giftcard_amount="{ record }">
        <span v-if="record.giftcard_amount > 0">
          ${{ record.giftcard_amount }}
        </span>
        <span v-else>-</span>
      </template>

      <template #detail_scoped="{ record }">
        <a-button type="link" @click="handleOpenDetailModal(record)">Ver detalles</a-button>
      </template>
    </a-table>

  </div>
  <a-modal
    :open="isOpenDetailModal"
    title="Detalles del referido"
    centered
    :width="560"
    :footer="null"
    :destroyOnClose="true"
    :maskClosable="false"
    class="ref-modal"
    @cancel="handleCloseDetailModal"
  >
    <div class="ref-detail">

      <div class="ref-list">
        <div class="ref-row">
          <div class="ref-label">Nombre del referido</div>
          <div class="ref-value">{{ detailBenefit?.first_name || '—' }}</div>
        </div>

        <div class="ref-row">
          <div class="ref-label">Apellido del referido</div>
          <div class="ref-value">{{ detailBenefit?.last_name || '—' }}</div>
        </div>

        <div class="ref-row">
          <div class="ref-label">Correo del referido</div>
          <div class="ref-value">{{ detailBenefit?.email || '—' }}</div>
        </div>

        <div class="ref-row">
          <div class="ref-label">Teléfono del referido</div>
          <div class="ref-value">
            {{ [detailBenefit?.code_country, detailBenefit?.phone_number].filter(Boolean).join(' ') || '—' }}
          </div>
        </div>

        <div class="ref-row">
          <div class="ref-label">Cargo del referido</div>
          <div class="ref-value">{{ detailBenefit?.charge || '—' }}</div>
        </div>

        <div class="ref-row">
          <div class="ref-label">Nombre de la empresa del referido</div>
          <div class="ref-value">{{ detailBenefit?.company || '—' }}</div>
        </div>

        <div class="ref-row">
          <div class="ref-label">Página web de la empresa del referido</div>
          <div class="ref-value">
            <a
              v-if="detailBenefit?.company_website"
              :href="detailBenefit?.company_website"
              target="_blank"
              rel="noopener"
              class="ref-link"
            >
              {{ detailBenefit?.company_website }}
            </a>
            <span v-else>—</span>
          </div>
        </div>

        <div class="ref-row">
          <div class="ref-label">Comentario adicional o contexto</div>
          <div class="ref-value">{{ detailBenefit?.comment || '—' }}</div>
        </div>
      </div>

      <div class="ref-footer">
        <a-button @click="handleCloseDetailModal">Cerrar detalle</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { defineProps, inject, ref, Ref } from "vue";
import { referralProgramTableColumn } from "@/app/referralProgram/utils/ReferralProgramColumn";
import { ReferredStatus, ReferredStatusColor, ReferredStatusIcon, ReferredStatusText } from "../../utils/enums";
import { Pagination } from "ant-design-vue";
import { ReferredUser } from "../../utils/IReferral";
import { Empty } from 'ant-design-vue';
const simpleImage = Empty.PRESENTED_IMAGE_SIMPLE;
const loadingTable = inject("loadingTable");
const { scrollStyle } = useScrollResize({ headerMargin: 350 });

defineProps({
  columnsTable: Array,
  usersInfo: Object,
  loadingTable: Boolean,
  scrollStyle: Object,
  dataSource: Array,
  pagination: Pagination,
});

const emit = defineEmits(['update', 'openRegisterNewBenefit']);
const currentTablePage = inject<Ref<number>>("currentTablePage") ?? ref(1);
const isOpenDetailModal = ref(false);
const handleTableChange = (pagination: any) => {
  currentTablePage.value = pagination.current;
  emit('update');
};
const detailBenefit = ref<ReferredUser>({
  first_name: "",
  last_name: "",
  email: "",
  code_country: '+51',
  phone_number: "",
  charge: "",
  company: "",
  company_website: "",
  comment: "",
});

const handleOpenDetailModal = (record:any) => {
  isOpenDetailModal.value = true;
  detailBenefit.value = record;
};

const handleCloseDetailModal = () => {
  isOpenDetailModal.value = false;
}
</script>

<style scoped lang="sass">
.table 
  width: 100%
  margin-top: 15px
.ref-modal :deep(.ant-modal-content)
  border-radius: 12px
  box-shadow: 0 8px 28px rgba(0,0,0,.15)

.ref-modal :deep(.ant-modal-header)
  border-bottom: none
  border-radius: 12px 12px 0 0
  padding: 16px 20px

.ref-modal :deep(.ant-modal-title)
  display: none // ocultamos el title nativo; usamos .ref-title

.ref-modal :deep(.ant-modal-body)
  padding: 8px 20px 0

.ref-detail
  display: flex
  flex-direction: column
  gap: 12px
  color: #1f1f1f

.ref-title
  font-size: 18px
  line-height: 1.3
  font-weight: 600
  margin: 4px 0 8px

.ref-list
  display: grid
  grid-auto-rows: min-content
  row-gap: 14px

.ref-row
  display: grid
  grid-template-columns: 1fr
  gap: 4px

.ref-label
  font-weight: 600
  font-style: Semibold
  font-size: 14px
  leading-trim: NONE
  line-height: 22px
  letter-spacing: 0%


.ref-value
  font-size: 14px
  color: #1f1f1f
  white-space: pre-wrap
  word-break: break-word

.ref-link
  text-decoration: underline
  text-underline-offset: 2px
  &:hover
    opacity: .85

.ref-footer
  display: flex
  justify-content: center
  padding: 18px 0 20px

  :deep(.ant-btn)
    min-width: 160px

.table :deep(.ant-empty)
  padding: 0

.empty-referrals
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  padding: 24px 12px
  text-align: center
  color: #8c8c8c
  font-size: 14px

.empty-referrals a
  color: #1677ff
  cursor: pointer
  text-decoration: underline
  text-underline-offset: 2px

.empty-icon
  width: 250px
  height: 56px
  margin-bottom: 120px
  color: #c2c2c2
  opacity: .6

.empty-text
  color: $gray-7
  a
    text-decoration: none

:where(.css-dev-only-do-not-override-19iuou).ant-empty
  margin-inline: -81px !important
</style>
